import {
  DownOutlined,
  HomeOutlined,
  LogoutOutlined,
  ProfileOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  Alert,
  App,
  Button,
  Dropdown,
  Flex,
  Layout,
  Result,
  Select,
  Tag,
  Tooltip,
  Typography,
  type MenuProps,
} from "antd";
import { useLocation, useNavigate, type RouteObject } from "react-router";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { useActiveWarehouse } from "@/lib/useWarehouses";
import { SubscriptionWarnings, useSubscription } from "@/pages/settings/Subscription";
import { useConnections } from "@/pages/channels/hooks";
import { LanguageSwitch } from "./LanguageSwitch";
import { Sidebar } from "./Sidebar";
import { WorkTabs } from "./tabs/WorkTabs";

function daysLeft(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export function AppShell({ routes }: { routes: RouteObject[] }) {
  const { t } = useI18n();
  const { me, signIn, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const connections = useConnections();
  const unhealthy = connections.data?.unhealthy ?? 0;

  if (!me?.workspace) return null;
  const ws = me.workspace;
  const left = ws.plan === "trial" ? daysLeft(ws.trialEndsAt) : null;

  const switchTo = async (workspaceId: string) => {
    try {
      const session = await unwrap(
        api.POST("/v1/workspaces/switch", { body: { workspaceId } }),
      );
      await signIn(session);
      navigate("/app");
    } catch (err) {
      void message.error(errorText(err));
    }
  };

  const others = me.workspaces.filter(
    (w) => w.id !== ws.id && w.status === "active",
  );
  const userMenu: MenuProps["items"] = [
    {
      key: "user",
      label: (
        <Typography.Text type="secondary">{me.user.email}</Typography.Text>
      ),
      disabled: true,
    },
    { type: "divider" },
    ...(others.length > 0
      ? [
          {
            key: "switch",
            icon: <SwapOutlined />,
            label: t("shell.switchWorkspace"),
            children: others.map((w) => ({ key: `ws:${w.id}`, label: w.name })),
          },
        ]
      : []),
    {
      key: "profile",
      icon: <ProfileOutlined />,
      label: t("shell.profile"),
      disabled: true,
    },
    { key: "logout", icon: <LogoutOutlined />, label: t("shell.logout") },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sidebar />
      <Layout>
        <Layout.Header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            height: 64,
          }}
        >
          <Flex align="center" gap={12}>
            <Typography.Text strong style={{ fontSize: 16 }}>
              {ws.name}
            </Typography.Text>
            <ActiveWarehouse />
            {left !== null && (
              <Tag color={left > 7 ? "success" : "warning"} variant="filled">
                {left > 0
                  ? t("shell.trialBadge", { days: left })
                  : t("shell.trialEnded")}
              </Tag>
            )}
          </Flex>
          <Flex align="center" gap={16}>
            <LanguageSwitch />
            <Dropdown
              trigger={["click"]}
              menu={{
                items: userMenu,
                onClick: ({ key }) => {
                  if (key === "logout")
                    void signOut().then(() => navigate("/login"));
                  else if (key.startsWith("ws:")) void switchTo(key.slice(3));
                },
              }}
            >
              <Button type="text">
                {me.user.name} <DownOutlined />
              </Button>
            </Dropdown>
          </Flex>
        </Layout.Header>
        <Layout.Content>
          <PlanWarnings />
          {me.support && (
            // Console Zerpid support session (Plan §6.16): read-only, visible.
            <Alert
              type="warning"
              banner
              title={t("support.banner", { name: me.support.staffName })}
              action={
                <Button
                  size="small"
                  onClick={() => void signOut().then(() => navigate("/login"))}
                >
                  {t("support.end")}
                </Button>
              }
            />
          )}
          {ws.status === "suspended" ? (
            <Result
              status="warning"
              title={t("shell.suspended.title")}
              subTitle={
                <>
                  {ws.suspendedReason && (
                    <Typography.Paragraph>
                      {ws.suspendedReason}
                    </Typography.Paragraph>
                  )}
                  <Typography.Text type="secondary">
                    {t("shell.suspended.body")}
                  </Typography.Text>
                </>
              }
            />
          ) : (
            <WorkTabs
              routes={routes}
              banner={
                // App Flow §12: marketplace sync trouble shows on every screen.
                unhealthy > 0 &&
                !location.pathname.startsWith("/app/channels") ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ margin: "16px 24px 0" }}
                    title={t("channels.banner", { n: unhealthy })}
                    action={
                      <Button
                        size="small"
                        onClick={() => navigate("/app/channels")}
                      >
                        {t("channels.bannerAction")}
                      </Button>
                    }
                  />
                ) : null
              }
            />
          )}
        </Layout.Content>
      </Layout>
    </Layout>
  );
}

/**
 * The active warehouse (Plan §6.14): every warehouse screen works in it.
 * Members of one warehouse see its name; others switch here, on purpose.
 */
function ActiveWarehouse() {
  const { t } = useI18n();
  const { can } = useAuth();
  const { current, accessible, set } = useActiveWarehouse();
  if (!can("warehouse.operate", "inventory.view") || !current) return null;
  if (accessible.length < 2) {
    return (
      <Tooltip title={t("shell.activeWarehouseHelp")}>
        <Tag icon={<HomeOutlined />} variant="filled">
          {current.name}
        </Tag>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={t("shell.activeWarehouseHelp")} placement="bottom">
      <Select<string>
        aria-label={t("shell.activeWarehouse")}
        value={current.id}
        onChange={set}
        prefix={<HomeOutlined />}
        popupMatchSelectWidth={false}
        style={{ minWidth: 200 }}
        options={accessible.map((w) => ({
          value: w.id,
          label: w.wmsEnabled ? `${w.name} · WMS` : w.name,
        }))}
      />
    </Tooltip>
  );
}

/** Plan warnings for whoever manages billing (Plan §6.17), on every screen. */
function PlanWarnings() {
  const { can, me } = useAuth();
  const allowed = can("billing.manage") && !me?.support;
  const sub = useSubscription(allowed);
  if (!allowed || !sub.data) return null;
  return <SubscriptionWarnings sub={sub.data} compact />;
}
